import React, { useState } from 'react';
import { 
  X, Info, BookOpen, Clock, Award, Settings, Paperclip, 
  ChevronRight, Calendar, User, FileText, CheckCircle2, Upload
} from 'lucide-react';

const CreateTestModal = ({
  isOpen,
  onClose,
  classes = [],
  subjects = [],
  teachers = [],
  testForm,
  setTestForm,
  onSubmit,
  editingTest
}) => {
  if (!isOpen) return null;

  const updateForm = (field, value) => {
    setTestForm(prev => ({ ...prev, [field]: value }));
  };

  const calculateDuration = () => {
    if (!testForm.startTime || !testForm.endTime) return 'N/A';
    const start = new Date(`1970-01-01T${testForm.startTime}`);
    const end = new Date(`1970-01-01T${testForm.endTime}`);
    let diff = (end - start) / 1000 / 60; // in minutes
    if (diff < 0) diff += 24 * 60; // handle cross-midnight
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours > 0 ? hours + ' hr ' : ''}${mins > 0 ? mins + ' min' : ''}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  const styles = {
    overlay: {
      position: 'absolute', top: '-16px', left: '-24px', right: '-24px', bottom: '-16px',
      backgroundColor: '#F8FAFC',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 50,
      animation: 'fadeIn 0.2s ease-out'
    },
    modal: {
      backgroundColor: '#F8FAFC',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden',
    },
    leftCol: {
      flex: 1,
      overflowY: 'auto',
      padding: '24px 40px',
    },
    formContainer: {
      width: '100%',
    },
    rightCol: {
      width: '320px',
      backgroundColor: '#FFFFFF',
      borderLeft: '1px solid #E2E8F0',
      display: 'flex',
      flexDirection: 'column',
      padding: '0'
    },
    section: {
      backgroundColor: '#FFFFFF',
      borderRadius: '12px',
      padding: '16px 20px',
      marginBottom: '16px',
      border: '1px solid #E2E8F0',
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '15px',
      fontWeight: '700',
      color: '#0F172A',
      marginBottom: '12px',
      borderBottom: '1px solid #F1F5F9',
      paddingBottom: '8px'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px',
      marginBottom: '12px'
    },
    formRow2: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      marginBottom: '12px'
    },
    formRow4: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '12px',
      marginBottom: '12px'
    },
    label: {
      display: 'block',
      fontSize: '12px',
      fontWeight: '600',
      color: '#475569',
      marginBottom: '4px'
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid #E2E8F0',
      fontSize: '13px',
      backgroundColor: '#FFFFFF',
      color: '#0F172A',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid #E2E8F0',
      fontSize: '13px',
      backgroundColor: '#FFFFFF',
      color: '#0F172A',
      outline: 'none',
      appearance: 'none',
      cursor: 'pointer',
      backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 12px center',
      backgroundSize: '14px',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid #E2E8F0',
      fontSize: '13px',
      backgroundColor: '#FFFFFF',
      color: '#0F172A',
      outline: 'none',
      resize: 'vertical',
      minHeight: '60px',
      boxSizing: 'border-box'
    },
    toggleRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: '1px solid #F1F5F9'
    },
    toggleSwitch: (isActive) => ({
      width: '36px',
      height: '20px',
      backgroundColor: isActive ? '#2563EB' : '#CBD5E1',
      borderRadius: '10px',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    }),
    toggleThumb: (isActive) => ({
      width: '16px',
      height: '16px',
      backgroundColor: '#FFFFFF',
      borderRadius: '50%',
      position: 'absolute',
      top: '2px',
      left: isActive ? '18px' : '2px',
      transition: 'left 0.2s',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
    }),
    uploadBox: {
      border: '2px dashed #E2E8F0',
      borderRadius: '8px',
      padding: '12px',
      textAlign: 'center',
      backgroundColor: '#F8FAFC',
      cursor: 'pointer',
      transition: 'background-color 0.2s, border-color 0.2s'
    },
    summaryItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '12px'
    },
    summaryIcon: {
      width: '28px',
      height: '28px',
      borderRadius: '6px',
      backgroundColor: '#F1F5F9',
      color: '#3B82F6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }
  };

  return (
    <div style={styles.overlay}>
      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .modern-input:focus { border-color: #3B82F6 !important; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important; }
        .upload-box:hover { border-color: #3B82F6 !important; background-color: #EFF6FF !important; }
        .btn-hover:hover { opacity: 0.9; transform: translateY(-1px); }
        .btn-hover:active { transform: translateY(0); }
        .custom-scroll::-webkit-scrollbar { width: 6px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
        .custom-scroll::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
      `}</style>

      <div style={styles.modal}>
        {/* LEFT COLUMN: Scrollable Form Sections */}
        <div style={styles.leftCol} className="custom-scroll">
          <div style={styles.formContainer}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '700', color: '#0F172A' }}>
                  {editingTest ? 'Edit Test / Exam' : 'Create Test / Exam'}
                </h2>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>Configure exam details, scheduling, and marks</p>
              </div>
            </div>

            <form id="create-test-form" onSubmit={handleSubmit}>
            
            {/* SECTION 1: Basic Information */}
            <div style={styles.section}>
              <div style={styles.sectionHeader}><Info size={16} color="#3B82F6" /> Basic Information</div>
              <div style={styles.formRow}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={styles.label}>Exam Title *</label>
                  <input 
                    className="modern-input" style={styles.input} required
                    placeholder="e.g., Mid Term Examination, Unit Test 1"
                    value={testForm.title || ''} onChange={e => updateForm('title', e.target.value)}
                  />
                </div>
                <div>
                  <label style={styles.label}>Exam Type</label>
                  <select className="modern-input" style={styles.select} value={testForm.examType || 'Term Exam'} onChange={e => updateForm('examType', e.target.value)}>
                    <option>Term Exam</option>
                    <option>Unit Test</option>
                    <option>Weekly Test</option>
                    <option>Mock Exam</option>
                  </select>
                </div>
              </div>
              <div style={styles.formRow}>
                <div>
                  <label style={styles.label}>Academic Year</label>
                  <input className="modern-input" style={styles.input} value={testForm.academicYear || '2026-2027'} onChange={e => updateForm('academicYear', e.target.value)} />
                </div>
                <div>
                  <label style={styles.label}>Term / Semester</label>
                  <select className="modern-input" style={styles.select} value={testForm.term || 'Semester 1'} onChange={e => updateForm('term', e.target.value)}>
                    <option>Semester 1</option>
                    <option>Semester 2</option>
                    <option>Term 1</option>
                    <option>Term 2</option>
                    <option>Term 3</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Description *</label>
                  <input 
                    className="modern-input" style={styles.input} required
                    placeholder="Short description..."
                    value={testForm.description || ''} onChange={e => updateForm('description', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* SECTION 2: Class Details */}
            <div style={styles.section}>
              <div style={styles.sectionHeader}><BookOpen size={16} color="#3B82F6" /> Class Details</div>
              <div style={styles.formRow4}>
                <div>
                  <label style={styles.label}>Class *</label>
                  <select className="modern-input" style={styles.select} required value={testForm.className || ''} onChange={e => updateForm('className', e.target.value)}>
                    <option value="">Select Class</option>
                    {Array.from(new Set(classes.map(c => c.className))).map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Section</label>
                  <select className="modern-input" style={styles.select} value={testForm.section || ''} onChange={e => updateForm('section', e.target.value)}>
                    <option value="">All Sections</option>
                    {['A', 'B', 'C', 'D', 'E'].map(sec => <option key={sec} value={sec}>{sec}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Subject *</label>
                  <select className="modern-input" style={styles.select} required value={testForm.subjectName || ''} onChange={e => updateForm('subjectName', e.target.value)}>
                    <option value="">Select Subject</option>
                    {subjects.map(s => (
                      <option key={s.subjectId || s.subjectCode} value={s.subjectName}>{s.subjectName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Teacher *</label>
                  <select className="modern-input" style={styles.select} required value={testForm.teacherId || ''} onChange={e => updateForm('teacherId', e.target.value)}>
                    <option value="">Select Assignee</option>
                    {teachers.map(t => (
                      <option key={t.teacherId} value={t.teacherId}>{t.teacherName || t.name || t.teacherId}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* SECTION 3 & 4: Schedule & Marks grouped side-by-side on large screens if possible, or just tight rows */}
            <div style={styles.formRow2}>
              <div style={styles.section}>
                <div style={styles.sectionHeader}><Clock size={16} color="#3B82F6" /> Schedule</div>
                <div style={styles.formRow}>
                  <div style={{ gridColumn: 'span 3' }}>
                    <label style={styles.label}>Exam Date *</label>
                    <input type="date" className="modern-input" style={styles.input} required value={testForm.testDate || ''} onChange={e => updateForm('testDate', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Start Time</label>
                    <input type="time" className="modern-input" style={styles.input} value={testForm.startTime || ''} onChange={e => updateForm('startTime', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>End Time</label>
                    <input type="time" className="modern-input" style={styles.input} value={testForm.endTime || ''} onChange={e => updateForm('endTime', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Duration</label>
                    <div style={{ padding: '8px', backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px dashed #CBD5E1', color: '#0F172A', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>
                      {calculateDuration()}
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.section}>
                <div style={styles.sectionHeader}><Award size={16} color="#3B82F6" /> Marks Configuration</div>
                <div style={styles.formRow4}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={styles.label}>Maximum Marks *</label>
                    <input type="number" min="1" className="modern-input" style={styles.input} required placeholder="100" value={testForm.maxMarks || ''} onChange={e => updateForm('maxMarks', e.target.value)} />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={styles.label}>Pass Marks</label>
                    <input type="number" min="0" className="modern-input" style={styles.input} placeholder="35" value={testForm.passMarks || ''} onChange={e => updateForm('passMarks', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Theory</label>
                    <input type="number" className="modern-input" style={styles.input} placeholder="80" value={testForm.theoryMarks || ''} onChange={e => updateForm('theoryMarks', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Practical</label>
                    <input type="number" className="modern-input" style={styles.input} placeholder="20" value={testForm.practicalMarks || ''} onChange={e => updateForm('practicalMarks', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Internal</label>
                    <input type="number" className="modern-input" style={styles.input} placeholder="0" value={testForm.internalMarks || ''} onChange={e => updateForm('internalMarks', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Weightage</label>
                    <input type="number" max="100" className="modern-input" style={styles.input} placeholder="100%" value={testForm.weightage || ''} onChange={e => updateForm('weightage', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 5 & 6: Settings & Attachments */}
            <div style={styles.formRow2}>
              <div style={styles.section}>
                <div style={styles.sectionHeader}><Settings size={16} color="#3B82F6" /> Exam Settings</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={styles.toggleRow}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>Publish Auto</div>
                    </div>
                    <div style={styles.toggleSwitch(testForm.publishAuto !== false)} onClick={() => updateForm('publishAuto', testForm.publishAuto === false)}>
                      <div style={styles.toggleThumb(testForm.publishAuto !== false)}></div>
                    </div>
                  </div>
                  <div style={styles.toggleRow}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>Visible to Parents</div>
                    </div>
                    <div style={styles.toggleSwitch(testForm.visibleToParents !== false)} onClick={() => updateForm('visibleToParents', testForm.visibleToParents === false)}>
                      <div style={styles.toggleThumb(testForm.visibleToParents !== false)}></div>
                    </div>
                  </div>
                  <div style={styles.toggleRow}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>Allow Re-exam</div>
                    </div>
                    <div style={styles.toggleSwitch(testForm.allowReExam === true)} onClick={() => updateForm('allowReExam', testForm.allowReExam !== true)}>
                      <div style={styles.toggleThumb(testForm.allowReExam === true)}></div>
                    </div>
                  </div>
                  <div style={styles.toggleRow}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>Format</div>
                    </div>
                    <select className="modern-input" style={{ ...styles.select, width: '80px', padding: '4px 8px', height: '24px', fontSize: '11px', backgroundPosition: 'right 4px center' }} value={testForm.onlineOffline || 'Offline'} onChange={e => updateForm('onlineOffline', e.target.value)}>
                      <option>Offline</option>
                      <option>Online</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={styles.section}>
                <div style={styles.sectionHeader}><Paperclip size={16} color="#3B82F6" /> Attachments</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="upload-box" style={styles.uploadBox}>
                    <Upload size={20} color="#64748B" style={{ margin: '0 auto 4px' }} />
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#0F172A' }}>Question Paper</div>
                  </div>
                  <div className="upload-box" style={styles.uploadBox}>
                    <Upload size={20} color="#64748B" style={{ margin: '0 auto 4px' }} />
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#0F172A' }}>Reference Docs</div>
                  </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <input 
                    className="modern-input" style={styles.input} 
                    placeholder="General exam instructions..."
                    value={testForm.instructions || ''} onChange={e => updateForm('instructions', e.target.value)}
                  />
                </div>
              </div>
            </div>
            
            {/* Hidden submit button to allow form submission via the external button */}
            <button type="submit" id="hidden-submit" style={{ display: 'none' }}></button>
          </form>
          </div>
        </div>

        {/* RIGHT COLUMN: Sticky Summary & Actions */}
        <div style={styles.rightCol}>
          {/* Header Actions */}
          <div style={{ padding: '24px', display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid #F1F5F9' }}>
            <button 
              onClick={onClose}
              style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748B', transition: 'all 0.2s' }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#F1F5F9'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = '#F8FAFC'}
            >
              <X size={18} />
            </button>
          </div>

          {/* Live Summary */}
          <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0F172A' }}>Exam Summary</h3>
            
            <div style={styles.summaryItem}>
              <div style={styles.summaryIcon}><FileText size={16} /></div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Exam Title</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>{testForm.title || '—'}</div>
              </div>
            </div>

            <div style={styles.summaryItem}>
              <div style={styles.summaryIcon}><User size={16} /></div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Class & Section</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>
                  {testForm.className ? `${testForm.className} ${testForm.section ? '- ' + testForm.section : ''}` : '—'}
                </div>
              </div>
            </div>

            <div style={styles.summaryItem}>
              <div style={styles.summaryIcon}><BookOpen size={16} /></div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Subject</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>{testForm.subjectName || '—'}</div>
              </div>
            </div>

            <div style={styles.summaryItem}>
              <div style={styles.summaryIcon}><Calendar size={16} /></div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Exam Date</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>{testForm.testDate ? new Date(testForm.testDate).toLocaleDateString('en-GB') : '—'}</div>
              </div>
            </div>

            <div style={styles.summaryItem}>
              <div style={styles.summaryIcon}><Clock size={16} /></div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Duration</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>{calculateDuration()}</div>
              </div>
            </div>

            <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#64748B' }}>Max Marks</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: '#0F172A' }}>{testForm.maxMarks || '0'}</div>
              </div>
              <Award size={32} color="#CBD5E1" />
            </div>
          </div>

          {/* Actions */}
          <div style={{ padding: '24px', borderTop: '1px solid #F1F5F9', backgroundColor: '#F8FAFC' }}>
            <button 
              className="btn-hover"
              onClick={onClose}
              style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', color: '#475569', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginBottom: '12px', transition: 'all 0.2s' }}
            >
              Cancel
            </button>
            <button 
              className="btn-hover"
              style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #BFDBFE', backgroundColor: '#EFF6FF', color: '#1E3A8A', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginBottom: '12px', transition: 'all 0.2s' }}
            >
              Save as Draft
            </button>
            <button 
              className="btn-hover"
              onClick={() => document.getElementById('create-test-form').requestSubmit()}
              style={{ width: '100%', padding: '12px', borderRadius: '12px', border: 'none', backgroundColor: '#2563EB', color: '#FFFFFF', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)', transition: 'all 0.2s' }}
            >
              <CheckCircle2 size={18} />
              {editingTest ? 'Save Changes' : 'Create Exam'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTestModal;
